import { useState, useCallback } from 'react';
import { formatShipDate } from '../utils/dates';

const useOrderApi = (baseUrl = '/api/v5') => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to handle API requests
  const makeRequest = useCallback(async (url, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData);
        console.log('Error data from response:', errorData);
        throw new Error(JSON.stringify(errorData));
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get countries
  const getCountries = useCallback(async (brandId, options = {}) => {
    const params = {
      brand_id: brandId,
      ...options
    };
    const searchParams = new URLSearchParams(params);
    const endpoint = `${baseUrl}/countries?${searchParams.toString()}`;
    return makeRequest(endpoint, { method: 'GET' });
  }, [baseUrl, makeRequest]);

  // Get products for a brand
  const getProducts = useCallback(async (brandId, options = {}) => {
    const params = {
      brand_id: brandId,
      product_type: 'label',
      public: true,
      active: true,
      ...options
    };
    const searchParams = new URLSearchParams(params);
    const endpoint = `${baseUrl}/products?${searchParams.toString()}`;
    return makeRequest(endpoint, { method: 'GET' });
  }, [baseUrl, makeRequest]);


  // Get packaging options for a product line
  const getPackaging = useCallback(async (productLine) => {
    const url = `${baseUrl}/packaging?product_line_id=${productLine.id}`;
    return makeRequest(url, { method: 'GET' });
  }, [baseUrl, makeRequest]);

  // Get service options for a product
  const getServiceOptions = useCallback(async (brandId, authToken, product, origin, destination) => {
    const url = `${baseUrl}/service_options`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
      },
      body: JSON.stringify({
        brand_id: brandId,
        product_id: product.id,
        origin: {
          state: origin.state,
          zip: origin.zip,
          country_code: origin.country_code || 'US'
        },
        destination: {
          state: destination.state,
          zip: destination.zip,
          country_code: destination.country_code || 'US'
        }
      })
    });
  }, [baseUrl, makeRequest]);

  const getCsrfToken = () => {
    try {
      const metaTag = document.querySelector('meta[name="csrf-token"]');
      if (metaTag) {
        return metaTag.getAttribute('content');
      }
      
      console.warn('CSRF token meta tag not found');
      return window.gon?.csrf_token || null;
    } catch (error) {
      console.error('Error getting CSRF token:', error);
      return null;
    }
  };

  // Create a new user
  const createUser = useCallback(async (authToken, brandId, userData) => {
    const url = `/${userData.hasTempPassword ? 'users/create_partial_user' : 'users/create_user'}`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-user-token': authToken,
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        user: {
          brand_id: brandId,
          first_name: userData.firstName,
          last_name: userData.lastName,
          email: userData.email,
          password: userData.password,
          phone_number: userData.phoneNumber,
          terms: userData.termsAccepted,  
          is_active: userData.isActive || true,
          has_temp_pass: userData.hasTempPassword || false
        }
      })
    });
  }, [makeRequest]);

  // Create a user session (login)
  const createSession = useCallback(async (brandId, email, password) => {
    const url = `${baseUrl}/sessions?brand_id=${brandId}`;
    return makeRequest(url, {
      method: 'POST',
      credentials: 'include', // This is crucial
      body: JSON.stringify({
        login_id: email,
        password: password
      })
    });
  }, [baseUrl, makeRequest]);

  // Create a shopping cart
  const createCart = useCallback(async (brandId, userId, adminId = null, giftCardIds = []) => {
    const url = `${baseUrl}/carts`;
    return makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        cart: {
          brand_id: brandId,
          user_id: userId,
          admin_id: adminId,
          device: 'web',
          gift_card_ids: giftCardIds
        }
      })
    });
  }, [baseUrl, makeRequest]);

  // Create a shopping cart
  const clearCart = useCallback(async (authToken, cart) => {
    if(!cart?.id) return;
    const url = `${baseUrl}/carts/${cart.id}/clear`;
    return makeRequest(url, {
      method: 'GET',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
         ...{ 'X-CSRF-Token': getCsrfToken() }
      },
    });
  }, [baseUrl, makeRequest]);

  // update the shopping cart
  const updateCart = useCallback(async (authToken, cart, params) => {
    if(!cart?.id) return;
    const url = `${baseUrl}/carts/${cart.id}`;
    return makeRequest(url, {
      method: 'PATCH',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
        'Content-Type': 'application/json',
         ...{ 'X-CSRF-Token': getCsrfToken() } 
      },
      body: JSON.stringify(params)
    });
  }, [baseUrl, makeRequest]);

  const getUserPaymentMethods = useCallback(async (formData) => {
    const url = `${baseUrl}/users/${formData.onBehalfUser?.id || formData.customerInfo?.id}/payments/methods/braintree`;
    return makeRequest(url, {
      method: 'GET',
      headers: {
        'x-user-token': formData.onBehalfUser?.authToken || formData.authToken,
        'x-cart-token': formData.cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      }
    });
  }, [baseUrl, makeRequest]);

  const createUserPaymentMethod = useCallback(async (formData, params) => {
    if(!formData.cart?.id) return;
    const url = `${baseUrl}/users/${formData.onBehalfUser?.id || formData.customerInfo?.id}/payments/methods`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': formData.authToken,
        'x-cart-token': formData.cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify(params)
    });
  }, [baseUrl, makeRequest]);

  const formatAttentionName = (origin, formData) => {
    if (origin.attention_name) return origin.attention_name;
    const firstName = formData.onBehalfUser?.firstName || formData.customerInfo?.firstName;
    const lastName = formData.onBehalfUser?.lastName || formData.customerInfo?.lastName;
    return (firstName || lastName) ? `${firstName || ''} ${lastName || ''}`.trim() : '';
  };

  // Create a ship route
  const createShipRoute = useCallback(async (formData, origin, destination) => {
    const url = `${baseUrl}/ship_routes`;

    const body = JSON.stringify({
        ship_route: {
          origin: {
            company_name: origin.company_name || origin.companyName || '',
            attention_name: formatAttentionName(origin, formData),
            phone_number: origin.phone_number,
            address_1: origin.address_1,
            address_2: origin.address_2,
            city: origin.city,
            state: origin.state,
            zip5: origin.zip,
            country_code: origin.country_code || 'US',
            cruise: origin.cruise || false,
            facility_id: origin.facility_id || null,
            logistics_provider_enabled: origin.logistics_provider_enabled || false,
            logistics_provider_id: origin.logistics_provider_id || null
          },
          destination: {
            company_name: destination.company_name || '',
            attention_name: formatAttentionName(destination, formData),
            phone_number: destination.phone_number,
            address_1: destination.address_1,
            address_2: destination.address_2,
            city: destination.city,
            state: destination.state,
            zip5: destination.zip,
            country_code: destination.country_code || 'US',
            cruise: destination.cruise || false,
            facility_id: destination.facility_id || null,
            logistics_provider_enabled: destination.logistics_provider_enabled || false,
            logistics_provider_id: destination.logistics_provider_id || null
          }
        }
      });
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': formData.authToken,
        'x-cart-token': formData.cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: body
    });
  }, [baseUrl, makeRequest]);

  // Create a leg for the ship route
  const createLeg = useCallback(async (
    authToken,
    cart,
    shipRouteId,
    legIndex = 1,
    pickup = false,
    readyTime = null,
    closeTime = null) => {
    const url = `${baseUrl}/legs`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        leg: {
          leg_index: legIndex.toString(),
          pickup: pickup,
          ship_route_id: shipRouteId,
          pickup_ready_time: readyTime,
          pickup_close_time: closeTime
        }
      })
    });
  }, [baseUrl, makeRequest]);

  const setCartUser = useCallback(async (authToken, cart, userId) => {
    const url = '/users/set_cart_user';
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        cart_id: cart.id,
        user_id: userId
      })
    });
  }, [makeRequest]);

  // Create a traveler
  const createTraveler = useCallback(async (authToken, cart, travelerData) => {
    const url = `${baseUrl}/users/travelers`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        traveler: {
          name: travelerData.name,
          email: travelerData.email,
          phone: travelerData.phone || travelerData.phoneNumber
        }
      })
    });
  }, [baseUrl, makeRequest]);

  // Get user products
  const getUserProducts = useCallback(async (authToken) => {
    const url = `${baseUrl}/users/products`;
    return makeRequest(url, {
      method: 'GET',
      headers: {
        'x-user-token': authToken,
      }
    });
  }, [baseUrl, makeRequest]);

  // Create a user product
  const createUserProduct = useCallback(async (authToken, productData) => {
    const url = `${baseUrl}/users/products`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
      },
      body: JSON.stringify({
        product: {
          name: productData.name,
          length: productData.length,
          width: productData.width,
          height: productData.height,
          weight: productData.weight,
          product_line_id: productData.productLineId,
          packaging_id: productData.packagingId,
          insurance_id: productData.insuranceId
        }
      })
    });
  }, [baseUrl, makeRequest]);

  // Get user product photos
  const getUserProductPhotos = useCallback(async (authToken, productId) => {
    const url = `${baseUrl}/users/products/${productId}/photos`;
    return makeRequest(url, {
      method: 'GET',
      headers: {
        'x-user-token': authToken,
      }
    });
  }, [baseUrl, makeRequest]);

  // Upload user product photo
  const uploadUserProductPhoto = useCallback(async (authToken, productId, imageFile) => {
    const url = `${baseUrl}/users/products/${productId}/photos`;
    
    // Convert file to base64
    const base64Image = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(imageFile);
    });

    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
      },
      body: JSON.stringify({
        image: base64Image
      })
    });
  }, [baseUrl, makeRequest]);

  // Get available dates for a route
  const getAvailableDates = useCallback(async (origin, destination, details = {}, options = {}) => {
    const url = `${baseUrl.replace('/v5', '/v6')}/dates`;
    
    const buildAddressParams = (address) => {
      // Handle cruise port addresses
      if (address?.cruise && address?.cruisePort) {
        const portAddress = address.cruisePort?.address || {};
        return {
          postal_code: portAddress.zip5 || portAddress.zip || portAddress.postal_code,
          country_code: portAddress.country_code || address.country_code || 'US',
          province: portAddress.state || portAddress.province,
         // facility_id: address.cruiseLine?.id || null
        };
      }
      
      // Standard address
      return {
        postal_code: address.zip || address.zip5 || address.postal_code,
        country_code: address.country_code || 'US',
        province: address.state || address.province,
        facility_id: address.facility_id || null
      };
    };

    return makeRequest(url, {
      method: 'POST',
      body: JSON.stringify({
        dates: {
          origin: buildAddressParams(origin),
          destination: buildAddressParams(destination),
          details: details,
          options: options
        }
      })
    });
  }, [baseUrl, makeRequest]);

  // Create cart item
  const createCartItem = useCallback(async (
    authToken,
    cart,
    onBehalfUserId,
    item
  ) => {
    const url = `${baseUrl}/carts/${cart.id}/items?cart_token=${cart.cart_token}`;

    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        on_behalf_user: onBehalfUserId || null,
        item: item
      })
    });

    // item: {
    //       ship_date: serviceOption.ship_date,
    //       desired_arrival_date: serviceOption.arrival_date,
    //       leg_id: leg.id,
    //       traveler_id: traveler.id,
    //       ship_route_id: shipRoute.id,
    //       packaging_id: packagingId,
    //       products: products
    //     }
  }, [baseUrl, makeRequest]);

  // Get cart details
  const getCart = useCallback(async (cart) => {
    const url = `${baseUrl}/carts/${cart.id}?cart_token=${cart.cart_token}`;
    return makeRequest(url, { method: 'GET' });
  }, [baseUrl, makeRequest]);

  // Create payment method
  const createPaymentMethod = useCallback(async (formData, paymentMethodNonce) => {
    const url = `${baseUrl}/users/${formData.onBehalfUser?.id || formData.customerInfo?.id}/payments/methods/braintree`;
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': formData.authToken,
      },
      body: JSON.stringify({
        payment_method_nonce: paymentMethodNonce
      })
    });
  }, [baseUrl, makeRequest]);

  // Get gift card
  const getGiftCard = useCallback(async (brandID, code) => {
    const url = `${baseUrl}/gift_up_payment_methods/${code}?brand_id=${brandID}`;
    return makeRequest(url, { method: 'GET' });
  }, [baseUrl, makeRequest]);

  // Create order
  const createOrder = useCallback(async (formData, paymentMethod) => {
    const { authToken, cart } = formData;
    const url = `${baseUrl.replace('/v5', '/v5.1')}/orders`;
    
    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
        'x-cart-token': cart.cart_token,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        cart_id: cart.id,
        cart_token: cart.cart_token,
        brand_id: formData.brandId,
        on_behalf_user: formData.onBehalfUser?.id || null,
        payment: {
          payment_method_id: paymentMethod.id,
        }
      })
    });
  }, [baseUrl, makeRequest]);

// Create order
  const getClubBillingClubs = useCallback(async (authToken) => {
    const url = `${baseUrl.replace('/v5', '/v6')}/club_billing`;

    return makeRequest(url, {
      method: 'GET',
      headers: {
        'x-user-token': authToken,  
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      }
    });
  }, [baseUrl, makeRequest]);

  // Create order
  const createReferral = useCallback(async (authToken, referral) => {
    const url = `${baseUrl.replace('/v5', '/v6')}/referrals`;

    return makeRequest(url, {
      method: 'POST',
      headers: {
        'x-user-token': authToken,
        'Content-Type': 'application/json',
        ...{ 'X-CSRF-Token': getCsrfToken() }
      },
      body: JSON.stringify({
        referral: referral
      })
    });
  }, [baseUrl, makeRequest]);

  const getFacilityInfo = (address) => {
    const facilityData = {}
    if(address?.facility_id) {
      // direct facility
      facilityData.facility_id = address.facility_id;
    }
    if(address?.cruise) {
      facilityData.cruise = true;
      facilityData.facility_id = address.cruiseLine?.id;
      facilityData.logistics_provider_id = address.cruisePort?.id;
      facilityData.logistics_provider_enabled = true;
      // take in the address component from the cruise port address
      const portAddress = address.cruisePort?.address || {};
      facilityData.address_1 = portAddress.delivery_address_line || '';
      facilityData.address_2 = portAddress.delivery_address_line_2 || '';
      facilityData.city = portAddress.city || '';
      facilityData.state = portAddress.state || '';
      facilityData.zip5 = portAddress.zip5 || portAddress.zip || portAddress.postal_code || '';
      facilityData.country_code = portAddress.country_code || address.country_code || 'US';
      facilityData.phone_number = portAddress.phone_number || portAddress.phone || 
              address.phone_number || address.phone;
      facilityData.attention_name = portAddress.attention_name || address.attention_name || '';
      facilityData.company_name = portAddress.company_name || address.company_name || address.cruiseLine?.name || '';
    }
    return facilityData;
  }

  const createShipLegFromFormData = useCallback(async (
    formData, fromAddress, toAddress, legIndex=1, pickup=true
  ) => {

    const shipRoute = await createShipRoute(
        formData, {
        company_name: fromAddress?.company_name,
        attention_name: fromAddress?.attention_name || fromAddress?.senderName,
        phone_number: fromAddress?.phone_number || fromAddress?.phoneNumber,
        address_1: fromAddress?.formatted_address?.split(',')?.[0] || fromAddress?.delivery_address_line,
        address_2: fromAddress?.address_2 || fromAddress?.address2 || '',
        city: fromAddress?.city,
        state: fromAddress?.state,
        zip: fromAddress?.zip5 || fromAddress?.zip || fromAddress?.postal_code || '00000',
        country_code: fromAddress?.country_code || 'US',
        ...getFacilityInfo(fromAddress)
      },
      {
        company_name: toAddress?.company_name,
        attention_name: toAddress?.attention_name || toAddress?.recipientName,
        phone_number: toAddress?.phone_number || toAddress?.phoneNumber,
        address_1: toAddress?.formatted_address?.split(',')?.[0] || toAddress?.delivery_address_line,
        address_2: toAddress?.address_2 || toAddress?.address2 || '',
        city: toAddress?.city,
        state: toAddress?.state,
        zip: toAddress?.zip5 || toAddress?.zip || toAddress?.postal_code || '00000',
        country_code: toAddress?.country_code || 'US',
        ...getFacilityInfo(toAddress)
      }
    );
    if (!shipRoute || !shipRoute.id) {
      throw new Error('Failed to create ship route');
    }
    const legData = formData.legs?.find(leg => leg.legIndex === (legIndex - 1)) || {};
    const pickupFee = legData.selectedPickupFee ? legData.selectedPickupFee : null;

    const leg = await createLeg(
      formData.authToken,
      formData.cart,
      shipRoute.id,
      legIndex,
      pickup,
      pickupFee ? pickupFee.ready_time : null,
      pickupFee ? pickupFee.close_time : null
    );
    if (!leg || !leg.id) {
      throw new Error('Failed to create leg');
    }
    return Promise.resolve(leg);
  }, [createShipRoute, createLeg]);

  const updateCartFromFormData = useCallback(async (formData) => {
    // if there are products selected we need to add them to the cart
    // we want to return a cart containing cart items
    const emptyResponse = Promise.resolve({});
    
    await clearCart(formData.authToken, formData.cart);

    const legData = formData.legs || [];
    if(!legData.length) {
      console.warn('No leg Data found.');
      return emptyResponse;
    }

    console.log('Updating cart from form data with legs:', legData);
    // this is all leg based now and we can create any number of legs
    // the products and services are all contained within the leg
    for (const leg of legData) {
      // the leg pricing rates contain the product lines rates
      // which contain packaging if needed
      const pricingRates = leg.pricing?.rates || []

      // these should have packaging on them
      const productsWithPackaging = leg.selectedProducts || [];

      if (!productsWithPackaging.length) {
        console.warn('No products with packaging available to add to cart.');
        return emptyResponse;
      }

      // console.log('Products with packaging:', productsWithPackaging);
      // lets get the traveler info
      const travelerName = formData.traveler?.name ||
        [formData.customerInfo?.firstName, formData.customerInfo?.lastName]
          .filter(Boolean)
          .join(' ').trim() || '';

      const traveler = await createTraveler(
        formData.authToken, formData.cart, 
        {
          name: travelerName,
          email: formData.traveler?.email || formData.customerInfo?.email || '',
          phone: formData.traveler?.phone || formData.traveler?.phoneNumber || formData.customerInfo?.phoneNumber || ''
        }
      );

      if (!traveler || !traveler.id) {
        throw new Error('Failed to create traveler');
      }

      // we are going to creaet the legs in order given
      // not really separating outbound and return
      const legService = leg.selectedService;
      const legIndex = leg.legIndex;

      const shipLeg = await createShipLegFromFormData(
        formData, 
        leg.fromAddress,
        leg.toAddress,
        legIndex + 1
      );  
      
      // use alternate ship date if provided
      const ship_date = 
        formatShipDate(leg.alternateShipDate, 'MM/DD/YYYY') || legService.ship_date;

      const serviceOptions = legService.service_options
     
      // now lets add each product to the cart
      for (const product of productsWithPackaging) {
        const productId = product.product_id || product.id;
        const serviceOption = serviceOptions.find(
          opt => {
            if (opt.product_id === productId || 
                opt.product?.id === productId ||
                opt.service_rate?.product_id === productId) {
              return true;
            }
            return false;
          }
        );

        for (let i = 0; i < (product.quantity || 1); i++) {
          // we need a cart item for each item in quantity
          let item_products = []
          item_products.push({
            product_id: productId,
            service_rate_id: serviceOption?.service_rate_id || serviceOption?.service_rate?._id
          });

          // insurance is a separate product
          if (product.insurance?.id) {
            item_products.push({
              product_id: product.insurance?.id,
            });
          }
          const productLine = pricingRates.find((rate) => {
            return (rate.name === product.product_line);
          });
          const packaging = product.packaging || 
            productLine?.packaging.find(p => p.name === 'Not Specified');

          // cruise itinerary
          const cruiseSessionId = leg.fromAddress?.selectedItinerary?.id || 
            leg.toAddress?.selectedItinerary?.id || null;

          const stateroom = leg.fromAddress?.stateroom || 
            leg.toAddress?.stateroom || '';

          const item = {
            ship_date: ship_date,
            desired_arrival_date: legService.arrival_date,
            leg_id: shipLeg.id,
            traveler_id: traveler.id,
            ship_route_id: shipLeg.ship_route.id,
            packaging_id: packaging?._id,
            cruise_session_id: cruiseSessionId,
            cabin: stateroom,
            products: item_products
          };
          await createCartItem(
            formData.authToken,
            formData.cart,
            formData.onBehalfUser?.id || null,
            item
          );
        }
      }

      if(leg.selectedPickupFee) {
        // we need to add the pickup fee as a cart item
        const pickupFee = leg.selectedPickupFee;
        const item = {
          type: 'pickup_fee',
          ship_date: ship_date,
          desired_arrival_date: legService.arrival_date,
          leg_id: shipLeg.id, 
          traveler_id: traveler.id,
          ship_route_id: shipLeg.ship_route.id,
          products: [
            {
              product_id: pickupFee.id,
            }
          ]
        };
        await createCartItem(
          formData.authToken,
          formData.cart,
          formData.onBehalfUser?.id || null,
          item
        );
      }
    }

    const couponCode = formData?.couponCode || null;
    if(couponCode) {
      // apply promo code to cart
      let giftCard = null;
      try {
        giftCard = await getGiftCard(formData.brandId, couponCode);
      } catch (error) {
        console.log('No gift card found for code:', couponCode);
      }
      const payload = 
        giftCard && giftCard.id ? 
        { gift_card_ids: [giftCard.id] } : 
        { coupon_code: couponCode };
        
      await updateCart(
        formData.authToken,
        formData.cart,
        { cart: payload }
      );
    }

    // finally get the updated cart with items
    const updatedCart = await getCart(formData.cart);
    return Promise.resolve(updatedCart);
  }, [
    clearCart,
    createTraveler,
    createShipLegFromFormData,
    createCartItem,
    getGiftCard,
    updateCart,
    getCart
  ]);
  
  return {
    loading,
    error,
    getCountries,
    getProducts,
    getPackaging,
    getAvailableDates,
    getServiceOptions,
    createUser,
    createSession,
    createCart,
    setCartUser,
    clearCart,
    updateCart,
    createShipRoute,
    createLeg,
    createTraveler,
    updateCartFromFormData,
    getUserProducts,
    createUserProduct,
    getUserProductPhotos,
    uploadUserProductPhoto,
    createCartItem,
    getCart,
    createPaymentMethod,
    getUserPaymentMethods,
    createUserPaymentMethod,
    getGiftCard,
    getClubBillingClubs,
    createOrder,
    createReferral
  };
};

export default useOrderApi;